import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const OPERATOR_ONLY_PATHS = [
  '/dashboard', '/customers', '/units', '/rentals', '/payments', '/leads',
  '/invoices', '/reports', '/assistant', '/site-map', '/automation',
  '/portfolio', '/intelligence', '/cameras', '/sms-ai', '/settings',
  '/document-signing',
];

const ProtectedRoute = ({ children, requireOwner = false, requireManager = false, requirePortfolioOwner = false }) => {
  const { user, loading, isAuthenticated, isOwner, isManager, isPortfolioOwner, isPlatformUser, impersonation } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Platform users in vendor mode (no impersonation) can only browse the
  // platform surface. If they hit an operator route directly via URL, send
  // them back to /platform-admin so they don't see a stale tenant view.
  if (isPlatformUser() && !impersonation) {
    const path = location.pathname;
    if (OPERATOR_ONLY_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
      return <Navigate to="/platform-admin" replace />;
    }
  }

  if (requireOwner && !isOwner()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">Owner privileges required. Your role: {user?.role}</p>
        </div>
      </div>
    );
  }

  // Portfolio rollup is portfolio-owner only. Facility-scoped users who
  // reach it via URL are redirected to their dashboard (soft visibility
  // rule, not a privilege error). Platform users impersonating an org
  // get portfolio visibility.
  if (
    requirePortfolioOwner &&
    !isPortfolioOwner() &&
    !(isPlatformUser() && impersonation)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireManager && !isManager()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">Manager or owner privileges required. Your role: {user?.role}</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
